import { ref, computed, h } from 'vue'
import DanmarksKortMap from './DanmarksKortMap.vue'
import FormInputCombobox from '../FormInputCombobox/FormInputCombobox.vue'
import { getTerminalLevel, formatEntityLabel } from './helpers/DanmarksKortMap.helpers.js'
import { COUNTRY_LABEL_FALLBACK } from './helpers/DanmarksKortMap.constants.js'

const StatList = {
  name: 'StatList',
  props: { stats: { type: Array, default: () => [] } },
  setup(props) {
    return () => h('dl', { class: 'kb-danmarks-kort__stats' },
      props.stats.map(stat => h('div', { class: 'kb-danmarks-kort__stat', key: stat.label }, [
        h('dt', null, stat.label),
        h('dd', null, stat.value)
      ]))
    )
  }
}

export default {
  name: 'DanmarksKort',
  components: { DanmarksKortMap, FormInputCombobox, StatList },
  props: {
    title:     { type: String, default: '' },
    flow:      { type: String, default: 'municipality' },
    groupData: { type: Array,  default: () => [] },
    legend:    { type: Array,  required: false },
    municipalityDataUrl: { type: String, default: '/api/map-data-{key}.json' },
    mapPanelLabel:             { type: String, default: 'Danmarkskort' },
    searchButtonLabel:         { type: String, default: 'Søg' },
    searchLabelCountry:        { type: String, default: 'Søg på region' },
    searchLabelMunicipality:   { type: String, default: 'Søg på kommune' },
    searchLabelSundhedsraad:   { type: String, default: 'Søg på sundhedsråd' },
    accessibilityListLabelMunicipality: { type: String, default: 'Vælg kommune' },
    accessibilityListLabelSundhedsraad: { type: String, default: 'Vælg sundhedsråd' },
    searchNoResultsLabel:      { type: String, default: 'Ingen resultater' },
    legendLabel:               { type: String, default: 'Signaturforklaring' },
    srTabLabelSundhedsraad: { type: String, default: 'Sundhedsråd' },
    srTabLabelKommuner:     { type: String, default: 'Kommuner' },
    srToggleAriaLabel:      { type: String, default: 'Vis data for sundhedsråd eller kommuner' },
    srSectionHeading:       { type: String, default: 'Se data for hele sundhedsrådet' },
    kommunerSectionHeading: { type: String, default: 'Se fordelingen pr kommune:' },
    municipalityNameSuffix: { type: String, default: 'Kommune' },
    disclaimerText:         { type: String, default: '' },
    loadErrorLabel:         { type: String, default: 'Kortdata kunne ikke indlæses. Prøv igen senere.' }
  },
  setup(props) {
    const mapLevel      = ref('country')
    const activeGroup   = ref(null)
    const activeDetail  = ref(null)
    const searchKeys    = ref([])
    const mapRef        = ref(null)

    const loadError        = ref(false)
    const activeSrTab      = ref('sundhedsraad')
    const openMunicipalities = ref(new Set())

    const onLoadError = () => { loadError.value = true }

    const onNavigate = ({ level, group, detail, options }) => {
      loadError.value     = false
      mapLevel.value      = level
      activeGroup.value   = group
      activeDetail.value  = detail
      searchKeys.value    = options ?? []
      if (detail?.kind !== 'sundhedsraad') {
        activeSrTab.value      = 'sundhedsraad'
        openMunicipalities.value = new Set()
      }
    }

    const searchOptions  = computed(() =>
      searchKeys.value.map(formatEntityLabel).sort((a, b) => a.localeCompare(b, 'da'))
    )
    const onSearchSelect = (label) =>
      mapRef.value?.selectByName(searchKeys.value.find(key => formatEntityLabel(key) === label) ?? label)

    const infoTitle = computed(() =>
      activeDetail.value?.label ?? activeGroup.value?.key ?? (props.title || COUNTRY_LABEL_FALLBACK)
    )
    const searchPlaceholder = computed(() => {
      if (mapLevel.value === 'country') return props.searchLabelCountry
      if (props.flow === 'sundhedsraad') return props.searchLabelSundhedsraad
      if (props.flow === 'region')       return props.searchLabelCountry
      return props.searchLabelMunicipality
    })
    const showSearch        = computed(() => mapLevel.value !== getTerminalLevel(props.flow))
    const showSrToggle      = computed(() => activeDetail.value?.kind === 'sundhedsraad')
    const isTabSundhedsraad = computed(() => activeSrTab.value === 'sundhedsraad')
    const isTabKommuner     = computed(() => activeSrTab.value === 'kommuner')
    const showSrStats       = computed(() => isTabSundhedsraad.value && !!activeDetail.value?.stats)
    const showSrSectionHeading = computed(() => showSrStats.value && activeDetail.value?.kind === 'sundhedsraad')
    const showKommunerList  = computed(() => isTabKommuner.value && !!activeDetail.value?.municipalities?.length)
    const srMunicipalities  = computed(() => activeDetail.value?.municipalities ?? [])

    const setSrTab = (tab) => {
      activeSrTab.value      = tab
      openMunicipalities.value = new Set()
    }
    const isMunicipalityOpen = (name) => openMunicipalities.value.has(name)
    const toggleMunicipality = (name) => {
      const next = new Set(openMunicipalities.value)
      next.has(name) ? next.delete(name) : next.add(name)
      openMunicipalities.value = next
    }

    return {
      activeDetail, activeSrTab, onNavigate, onLoadError, loadError, infoTitle, searchPlaceholder,
      searchOptions, mapRef, onSearchSelect, isMunicipalityOpen,
      showSearch, showSrToggle, isTabSundhedsraad, isTabKommuner,
      showSrStats, showSrSectionHeading, showKommunerList, srMunicipalities,
      setSrTab, toggleMunicipality
    }
  }
}
